/**
 * ETQ back in stock in one step (Melvin 1 Nov: "ik wil gewoon gelijk dat e-mailveld zien"). The live
 * flow (popups product.back-in-stock → -email, inventory/live/assets/product.js .js-bis-form) needed
 * three taps before the visitor could type; here the email field is already on the page:
 * - main picker: a sold-out size tile shows snippets/etq-bis-form where the add button sits (the button
 *   is hidden by assets/etq-pdp.css while it shows) and focuses the field (the tap is the user gesture,
 *   so the keyboard opens at once). An in-stock size brings the normal add button back. blocks/add-to-cart
 *   renders the right state on page load; after a tile pick Horizon only morphs part of the form, so the
 *   state is derived again from the chosen tile whenever the form changes (idempotent, no loops);
 * - "Choose size" sheet: a sold-out row opens the same field under that row (no second sheet) and
 *   focuses it; the event `etq:bis-open` ({ variantId, inSheet }) comes from assets/etq-pdp.js;
 * - sticky bar "Notify me" (inSheet false): scrolls to the field at the add button and focuses it;
 * - the Klaviyo subscription is posted exactly as live: client endpoint, company WWd9K8, revision
 *   2024-07-15, profile email, channel EMAIL, variant "$shopify:::$default:::<variant id>". No list or
 *   consent fields (live has none). Klaviyo answers 202; any other answer shows the error line.
 * The field sits inside Horizon's product <form>, so there is no nested form: the button is
 * type="button" and Enter is handled here (it never submits the add form).
 */

const KLAVIYO_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=WWd9K8';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

const sizeSheet = /** @type {HTMLDialogElement | null} */ (document.getElementById('etq-size-sheet'));
const productId =
  document.querySelector('[data-etq-sticky-atc]')?.getAttribute('data-product-id') ?? sizeSheet?.dataset.productId ?? '';

/** @type {Map<string, string>} variant id → email it was subscribed with (kept while the page lives) */
const subscribed = new Map();
let typedEmail = '';
let lastPointer = 0;

/* ---------- The page's own product form ---------- */

/** @returns {HTMLElement | null} */
function mainForm() {
  for (const element of document.querySelectorAll('product-form-component')) {
    if (!(element instanceof HTMLElement) || element.dataset.productId !== productId) continue;
    if (element.closest('dialog, product-card, sticky-add-to-cart, quick-add-component')) continue;
    return element;
  }
  return null;
}

/** @returns {HTMLElement | null} The email block at the add button */
function inlineBox() {
  const box = mainForm()?.querySelector('[data-etq-bis-form]');
  return box instanceof HTMLElement ? box : null;
}

/** @returns {HTMLInputElement | null} The chosen size tile of the main picker */
function chosenTile() {
  const input = document.querySelector('variant-picker[data-template-product-match] fieldset.etq-size input:checked');
  return input instanceof HTMLInputElement ? input : null;
}

/**
 * @param {string} variantId
 * @param {HTMLInputElement | null} [tile]
 * @returns {string} The size in the visitor's system ("EU 46"), from the size sheet's row
 */
function sizeLabel(variantId, tile) {
  const row = sizeSheet?.querySelector(`button[data-etq-variant-id="${CSS.escape(variantId)}"]`);
  if (row instanceof HTMLElement && row.dataset.etqSizeLabel) return row.dataset.etqSizeLabel;
  return (tile?.value ?? '').split('|')[0]?.trim() ?? '';
}

/* ---------- The email block (inline and in the size sheet) ---------- */

/**
 * @param {HTMLElement} box
 * @param {string} selector
 * @returns {HTMLElement | null}
 */
function part(box, selector) {
  const element = box.querySelector(selector);
  return element instanceof HTMLElement ? element : null;
}

/** @param {HTMLElement} box */
function emailInput(box) {
  const input = box.querySelector('[data-etq-bis-input]');
  return input instanceof HTMLInputElement ? input : null;
}

/**
 * @param {HTMLElement} box
 * @param {boolean} on
 */
function setError(box, on, pickSize = false) {
  const error = part(box, '[data-etq-bis-error]');
  if (!error) return;
  if (!error.dataset.etqDefault) error.dataset.etqDefault = error.textContent?.trim() ?? '';
  const text = pickSize ? error.dataset.etqPickSize || error.dataset.etqDefault : error.dataset.etqDefault;
  if (error.textContent?.trim() !== text) error.textContent = text;
  if (error.hidden === on) error.hidden = !on;
}

/**
 * Brings a block in line with a variant: its size line, the ask or the success state. Idempotent, so
 * it can run after every DOM change without causing another one.
 * @param {HTMLElement} box
 * @param {string} variantId
 * @param {string} label
 */
function render(box, variantId, label) {
  if (box.hidden) box.hidden = false;
  if ((box.dataset.etqVariantId ?? '') !== variantId) {
    box.dataset.etqVariantId = variantId;
    setError(box, false);
  }

  const size = part(box, '[data-etq-bis-size]');
  const status = part(box, '[data-etq-bis-status]');
  if (size && size.textContent !== label) size.textContent = label;
  if (status && status.hidden === Boolean(label)) status.hidden = !label;

  const input = emailInput(box);
  if (input && !input.value && typedEmail) input.value = typedEmail;

  const email = subscribed.get(variantId);
  const done = part(box, '[data-etq-bis-done]');
  const ask = part(box, '[data-etq-bis-ask]');
  if (done && ask) {
    if (done.hidden === Boolean(email)) done.hidden = !email;
    if (ask.hidden !== Boolean(email)) ask.hidden = Boolean(email);
    const strong = part(done, '[data-etq-bis-email]');
    if (email && strong && strong.textContent !== email) strong.textContent = email;
  }
}

/** @param {HTMLElement} box */
function hide(box) {
  if (!box.hidden) box.hidden = true;
}

/** @param {HTMLElement} box */
function focusField(box) {
  const input = emailInput(box);
  if (input && !box.hidden && !part(box, '[data-etq-bis-ask]')?.hidden) input.focus({ preventScroll: true });
}

/* ---------- Main picker: the block follows the chosen size ---------- */

/**
 * Brings the add button and the email block in line with the chosen tile. Without a chosen tile the
 * page-load state stays as the server rendered it (a sold-out product shows the block, no size yet).
 */
function syncInline() {
  const form = mainForm();
  const box = inlineBox();
  const tile = chosenTile();
  if (!form || !box) return;

  const button = form.querySelector('button[name="add"]');
  if (!tile) {
    if (!box.hidden) render(box, box.dataset.etqVariantId ?? '', '');
    return;
  }

  const soldOut = tile.dataset.optionAvailable === 'false';
  const id = tile.dataset.variantId ?? '';
  if (button instanceof HTMLButtonElement) {
    if (button.hasAttribute('data-etq-notify') !== soldOut) button.toggleAttribute('data-etq-notify', soldOut);
    if (soldOut && button.dataset.etqVariantId !== id) button.dataset.etqVariantId = id;
  }
  if (soldOut) render(box, id, sizeLabel(id, tile));
  else hide(box);
}

/**
 * Instant add-button label while Horizon fetches the new variant (it then morphs in the server's
 * button): "Notify me when available" for a sold-out size, the add label otherwise
 * @param {HTMLInputElement} tile
 */
function reflectTile(tile) {
  const soldOut = tile.dataset.optionAvailable === 'false';
  const addLabel = document.documentElement.dataset.etqAddLabel ?? '';
  const button = mainForm()?.querySelector('button[name="add"]');
  if (button instanceof HTMLButtonElement) {
    const label = button.querySelector('.etq-atc__label');
    const text = soldOut ? (inlineBox()?.querySelector('[data-etq-bis-submit]')?.textContent?.trim() ?? '') : addLabel;
    if (label && text && label.textContent !== text) label.textContent = text;
  }
  syncInline();
}

/**
 * Horizon disables the button for an unavailable variant and morphs only its children (label), never
 * its own attributes; a sold-out button must stay clickable (it then focuses the field), so lift that
 * disabled state. Also re-derives the block after every change Horizon makes to the form.
 */
function watchForm() {
  const form = mainForm();
  if (!form) return;
  let queued = false;
  const run = () => {
    queued = false;
    for (const button of mainForm()?.querySelectorAll('button[name="add"][data-etq-notify]') ?? []) {
      if (button instanceof HTMLButtonElement && button.disabled) button.disabled = false;
    }
    syncInline();
  };
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(run);
  }).observe(form.closest('.shopify-section, section') ?? form, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'data-etq-notify', 'hidden', 'data-option-available'],
  });
  run();
}

// A pointer tap on a tile (not an arrow key through the sizes) should open the keyboard at once
document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.target instanceof Element && event.target.closest('variant-picker fieldset.etq-size')) {
      lastPointer = performance.now();
    }
  },
  true
);

// Registered after assets/etq.js (module order), so this label wins over its "Add to bag"
document.addEventListener('change', (event) => {
  const tile = event.target;
  if (!(tile instanceof HTMLInputElement)) return;
  if (!tile.closest('variant-picker[data-template-product-match] fieldset.etq-size')) return;
  reflectTile(tile);
  const box = inlineBox();
  if (box && !box.hidden && tile.dataset.optionAvailable === 'false' && performance.now() - lastPointer < 600) {
    focusField(box);
  }
});

// Capture phase, so Horizon never tries to add the sold-out size (the size guard in etq.js, which
// also listens in the capture phase, runs first while no size is chosen). Only reachable through
// code (the sticky bar on a sold-out product): the button itself is hidden while the field shows.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name="add"][data-etq-notify]') : null;
    if (!(button instanceof HTMLButtonElement) || button.closest('product-form-component') !== mainForm()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    goToInline();
  },
  true
);

/** Scrolls to the email block at the add button and focuses it */
function goToInline() {
  syncInline();
  const box = inlineBox();
  if (!box || box.hidden) return;
  box.scrollIntoView({ block: 'center', behavior: REDUCED.matches ? 'auto' : 'smooth' });
  focusField(box);
}

/* ---------- Size sheet: the block opens under the tapped sold-out row ---------- */

const slot = sizeSheet?.querySelector('[data-etq-bis-slot]');

/** @returns {HTMLElement | null} */
function sheetBox() {
  const box = slot?.querySelector('[data-etq-bis-form]');
  return box instanceof HTMLElement ? box : null;
}

function closeSheetBox() {
  if (slot instanceof HTMLElement) slot.hidden = true;
  const box = sheetBox();
  if (box) hide(box);
  for (const row of sizeSheet?.querySelectorAll('button[data-etq-variant-id][aria-expanded]') ?? []) {
    row.removeAttribute('aria-expanded');
  }
}

/** @param {string} variantId */
function openInSheet(variantId) {
  const box = sheetBox();
  const row = sizeSheet?.querySelector(`button[data-etq-variant-id="${CSS.escape(variantId)}"]`);
  const item = row?.closest('li');
  if (!(slot instanceof HTMLElement) || !box || !row || !item) return;

  // The same row again closes the field
  if (!slot.hidden && row.getAttribute('aria-expanded') === 'true') {
    closeSheetBox();
    return;
  }
  closeSheetBox();
  item.after(slot);
  slot.hidden = false;
  row.setAttribute('aria-expanded', 'true');
  render(box, variantId, '');
  slot.scrollIntoView({ block: 'nearest', behavior: REDUCED.matches ? 'auto' : 'smooth' });
  focusField(box);
}

sizeSheet?.addEventListener('close', closeSheetBox);

document.addEventListener('etq:bis-open', (event) => {
  const detail = /** @type {CustomEvent<{variantId?: string, inSheet?: boolean}>} */ (event).detail;
  const id = String(detail?.variantId ?? '');
  if (!id) return;
  if (detail?.inSheet) openInSheet(id);
  else goToInline();
});

/* ---------- Klaviyo ---------- */

/**
 * @param {string} email
 * @param {string} variantId
 */
function payload(email, variantId) {
  return {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        profile: { data: { type: 'profile', attributes: { email } } },
        channels: ['EMAIL'],
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
      },
    },
  };
}

/** @param {HTMLElement} box */
async function submit(box) {
  const input = emailInput(box);
  const variantId = box.dataset.etqVariantId ?? '';
  const email = input?.value.trim() ?? '';
  if (!variantId) {
    setError(box, true, true);
    return;
  }
  if (!input || !EMAIL_PATTERN.test(email) || !input.checkValidity()) {
    setError(box, true);
    input?.focus({ preventScroll: true });
    return;
  }
  setError(box, false);

  const button = part(box, '[data-etq-bis-submit]');
  if (button instanceof HTMLButtonElement) {
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  try {
    const response = await fetch(KLAVIYO_URL, {
      method: 'POST',
      headers: { accept: 'application/json', revision: '2024-07-15', 'content-type': 'application/json' },
      body: JSON.stringify(payload(email, variantId)),
    });
    if (!response.ok) throw new Error(String(response.status));
    subscribed.set(variantId, email);
    typedEmail = email;
    render(box, variantId, part(box, '[data-etq-bis-size]')?.textContent ?? '');
    syncInline(); // the same size may also show at the add button (subscribed from the size sheet)
  } catch {
    setError(box, true);
  } finally {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-etq-bis-submit]');
  const box = button?.closest('[data-etq-bis-form]');
  if (!(box instanceof HTMLElement)) return;
  event.preventDefault();
  event.stopPropagation();
  submit(box);
});

// Enter sends the subscription; inside Horizon's product form it must never submit the add form
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = event.target instanceof Element ? event.target.closest('[data-etq-bis-input]') : null;
    const box = input?.closest('[data-etq-bis-form]');
    if (!(box instanceof HTMLElement)) return;
    event.preventDefault();
    event.stopPropagation();
    submit(box);
  },
  true
);

document.addEventListener('input', (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-etq-bis-input]') : null;
  const box = input?.closest('[data-etq-bis-form]');
  if (!(input instanceof HTMLInputElement) || !(box instanceof HTMLElement)) return;
  typedEmail = input.value.trim();
  setError(box, false);
});

watchForm();
